import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import * as React from 'react';

import { LocalizedComponentProps, LocalizedText } from 'bernie-l10n';

import { useLocalization } from '@shared-ui/localization-context';

import { SearchResultsStore } from 'stores/search-results-store';

import { SearchFormFields } from 'models';

import { UitkDialogAnimationType, UitkDialogContent, UitkDialogProps, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkTertiaryButton } from 'uitk-react-button';
import { UitkToolbar } from 'uitk-react-toolbar';

import { SearchForm, SearchFormProps } from 'components/common/search-form';
import { SearchFormBottomSection } from 'components/search/search-form-bottom-section';

export interface FiltersDialogProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  searchResults: SearchResultsStore;
  onDismiss: () => void;
  filters: SearchFormFields;
  dialogTitle: string;
  showSeparator?: boolean;
}

const FiltersDialogComponent: React.FunctionComponent<FiltersDialogProps> = props => {
  const buttonRef = React.useRef();
  const l10n = useLocalization();

  let formRef;
  const formRefCallback = ref => {
    if (ref) {
      formRef = ref.wrappedInstance ? ref.wrappedInstance : ref;
    } else {
      formRef = null;
    }
  };

  const closeDialog = () => {
    if (typeof props.onDismiss === 'function') {
      props.onDismiss();
    }
  };

  const clearFilters = () => {
    const searchForm = formRef;

    if (searchForm && typeof searchForm.clearDisplayfields === 'function') {
      searchForm.clearDisplayfields();
    }
  };

  const getSearchFormBottomSection = () => <SearchFormBottomSection buttonRef={buttonRef} />;

  const getSearchFormTopSection = () => {
    const { analytics, dialogTitle } = props;
    const displayFieldsActiveLength = formRef?.displayFieldsActiveLength;
    const clearButtonActive = displayFieldsActiveLength > 0;
    const toolbarProps = {
      action: closeDialog,
      iconLabel: l10n.formatText('hotels.searchResults.filtersDialog.closeButton.label'),
      header: dialogTitle,
      analytics: analytics?.setupTracker('CloseFilterDialog', 'close search filters dialog'),
      actionContent: (
        <UitkTertiaryButton type="reset" disabled={!clearButtonActive} onClick={clearFilters}>
          <LocalizedText message="hotels.searchResults.filtersDialog.clearButton.label" />
        </UitkTertiaryButton>
      ),
    };

    return <UitkToolbar {...toolbarProps} />;
  };

  const { searchResults, filters, showSeparator } = props;
  const searchResultsCriteria = searchResults?.searchCriteria;
  const sortAndFilter = searchResults?.sortAndFilter;
  const sortAndFilterOptions = sortAndFilter?.options;
  const sortAndFilterDefaults = sortAndFilter?.defaults;
  const displayFields = filters?.displayFields;
  const ignoreFields = filters?.ignoreFields;
  const dialogProps: Partial<UitkDialogProps> = {
    dialogShow: true,
    animation: UitkDialogAnimationType.SLIDEUP,
    returnFocusOnClose: true,
  };

  // field name override will not run without BFF bucketed displayFields
  const fieldNameOverrides = {
    amenityIcons: 'amenities', // Exp 28075: LS_Amenity_Filter_Improvement
  };

  const searchFormProps: SearchFormProps = {
    formRef: formRefCallback,
    model: searchResultsCriteria,
    sortAndFilterOptions: sortAndFilterOptions,
    sortAndFilterDefaults: sortAndFilterDefaults,
    renderSearchFormTopSection: getSearchFormTopSection,
    renderSearchFormBottomSection: getSearchFormBottomSection,
    provideDefaultSubmitButton: false,
    displayFields: displayFields,
    fieldsWrapper: {
      Tag: UitkDialogContent,
    },
    ignoreFields: ignoreFields,
    fieldNameOverrides: fieldNameOverrides,
    searchable: true,
    showSeparator: showSeparator,
  };

  return (
    <UitkFullscreenDialog {...dialogProps}>
      <UitkDialogContent zeroPadding={true}>
        <UitkSpacing padding={{ blockend: 'twelve' }}>
          <SearchForm {...searchFormProps} />
        </UitkSpacing>
      </UitkDialogContent>
      <span className="is-visually-hidden">
        <LocalizedText message="hotels.searchResults.filtersDialog.a11yLabel" />
      </span>
    </UitkFullscreenDialog>
  );
};

export const FiltersDialog = inject('analytics', 'searchResults')(FiltersDialogComponent);
